import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { NewTabLink } from "components/NewTabLink";
import { formatPendingOffPlatformBookingRate } from "components/Shipper/components/MarkBookedOffPlatformModal";

import {
  BOOKED_OFF_PLATFORM_TEXT,
  ONLY_LOOKING_FOR_QUOTES_TEXT,
  OTHER_REASON_OPTION,
} from "./constants";
import { OffPlatformBookingCancellationPrompt } from "./OffPlatformBookingCancellationPrompt";
import { QUOTES_ONLY_SHIPMENT_BLOG_URL } from "../../../../constants";

import type { PendingOffPlatformBooking } from "hooks";

interface Props {
  selectedOption: string | null;
  onSelectedOptionChange: (option: string) => void;
  cancellationReason: string;
  onCancellationUpdate: (newValue: string) => void;
  loading: boolean;
  error: string | null;
  shipment: CondensedShipment;
  // Only provided when the shipment can take an off-platform booking report.
  onRecordOffPlatformBooking?: VoidFunction;
  pendingOffPlatformBooking: PendingOffPlatformBooking | null;
}

export function CancelShipmentFormModalContent(props: Props) {
  const {
    selectedOption,
    onSelectedOptionChange,
    loading,
    cancellationReason,
    onCancellationUpdate,
    error,
    shipment,
    onRecordOffPlatformBooking,
    pendingOffPlatformBooking,
  } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectedOptionChange(event.target.value);
  };

  if (loading) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (shipment.info.type === "QUOTES_ONLY") {
    return (
      <Stack direction="column" spacing={1}>
        <Typography variant="body1">
          Are you sure you want to cancel this shipment?
        </Typography>

        {error != null && !loading && (
          <Typography color="red" align="center">
            {error}
          </Typography>
        )}
      </Stack>
    );
  }

  // QUOTES_ONLY is handled above and can never carry a report: the report
  // endpoint rejects that shipment type.
  return (
    <Stack direction="column" spacing={1}>
      {pendingOffPlatformBooking !== null && (
        <Alert severity="warning">
          {`Cancelling this shipment will prevent support from recording the off-platform rate of ${formatPendingOffPlatformBookingRate(
            pendingOffPlatformBooking,
          )}.`}
        </Alert>
      )}
      <Typography variant="h5">
        Why are you cancelling this shipment?
      </Typography>
      <Typography variant="body2" color="text.secondary">
        The cancellation reason will be shared with carriers who quoted on this
        shipment. This action can not be reversed.
      </Typography>
      <Box mb="2rem">
        <FormControl>
          <RadioGroup value={selectedOption} onChange={handleChange}>
            <FormControlLabel
              value="Customer cancelled the order"
              control={<Radio />}
              label="Customer cancelled the order"
            />
            <FormControlLabel
              value="I did not receive any suitable quotes"
              control={<Radio />}
              label="I did not receive any suitable quotes"
            />
            <FormControlLabel
              value={ONLY_LOOKING_FOR_QUOTES_TEXT}
              control={<Radio />}
              label="The product is not ready/I was only looking for quotes at this time"
            />
            {selectedOption === ONLY_LOOKING_FOR_QUOTES_TEXT && (
              <Typography variant="body1" color="textSecondary">
                For your next price discovery shipment, consider{" "}
                <NewTabLink href={QUOTES_ONLY_SHIPMENT_BLOG_URL}>
                  quote only shipments
                </NewTabLink>
                .
              </Typography>
            )}
            <FormControlLabel
              value={BOOKED_OFF_PLATFORM_TEXT}
              control={<Radio />}
              label={BOOKED_OFF_PLATFORM_TEXT}
            />
            {selectedOption === BOOKED_OFF_PLATFORM_TEXT &&
              onRecordOffPlatformBooking !== undefined && (
                <OffPlatformBookingCancellationPrompt
                  onRecordBooking={onRecordOffPlatformBooking}
                />
              )}
            <FormControlLabel
              value={OTHER_REASON_OPTION}
              control={<Radio />}
              label="Other"
            />
          </RadioGroup>
        </FormControl>
      </Box>
      {selectedOption === OTHER_REASON_OPTION && (
        <TextField
          id="cancellation-reason"
          variant="outlined"
          onChange={(e) => onCancellationUpdate(e.target.value)}
          value={cancellationReason}
        />
      )}
      {error != null && !loading && (
        <Typography color="red" align="center">
          {error}
        </Typography>
      )}
    </Stack>
  );
}
